import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  GiftCardStatus,
  GiftCardStatusEnum,
  useChangeGiftCardStatus,
} from '@/api/gift-cards/change-gift-card-status'
import { Button } from '@/components/ui/button'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { Modal } from '@/components/ui/modal'

import { useGetGiftcardById } from '../../../../api/gift-cards/get-gift-card-by-id'
import { selectUserRole } from '../../../../reducers/userDataReducer'
import DetailsItem from '../components/DetailsItem/DetailsItem'
import { mapGiftCardDetails, mapRecipientDetails, mapTransactionDetails } from '../helpers'

type RewardsGiftDetailsModalProps = {
  timezone: string
  id?: number
  onClose: () => void
  fetchRewards?: () => Promise<void> // Note: Remove once old gift cards page is removed
}

const RewardsGiftDetailsModal = ({
  timezone,
  id,
  onClose,
  fetchRewards,
}: RewardsGiftDetailsModalProps) => {
  const { t } = useTranslation(['rewards-gift', 'rewards-table', 'common'])
  const userRole = useSelector(selectUserRole)

  const [newStatus, setNewStatus] = useState<GiftCardStatus>()

  const { data: giftCard, isLoading } = useGetGiftcardById(id)
  const { mutateAsync: changeStatus } = useChangeGiftCardStatus()

  const isManagerOrAdmin = ['Manager', 'Administrator'].includes(userRole)
  const canChangeStatus = giftCard?.type === 'service' && isManagerOrAdmin
  const isGiftCardExpired = giftCard?.expirationDate
    ? giftCard?.expirationDate < new Date().getTime()
    : false
  const shouldShowSaveButton = canChangeStatus && giftCard?.status !== newStatus && !isLoading
  const statusOptions = [
    {
      disabled: isGiftCardExpired || newStatus === GiftCardStatusEnum.enum['not-redeemed'],
      label: t('rewards-table:giftCardStatuses.notRedeemed'),
      value: GiftCardStatusEnum.enum['not-redeemed'],
    },
    {
      disabled: newStatus === GiftCardStatusEnum.enum['redeemed'],
      label: t('rewards-table:giftCardStatuses.redeemed'),
      value: GiftCardStatusEnum.enum['redeemed'],
    },
    ...(isGiftCardExpired
      ? [
          {
            disabled: newStatus === GiftCardStatusEnum.enum['expired'],
            label: t('rewards-table:giftCardStatuses.expired'),
            value: GiftCardStatusEnum.enum['expired'],
          },
        ]
      : []),
  ]

  const handleChangeStatus = async () => {
    if (!id || !newStatus) return

    await changeStatus({ giftCardId: id, newStatus })
    await fetchRewards?.()
  }

  const items = [
    {
      data: mapGiftCardDetails(
        giftCard,
        timezone,
        isManagerOrAdmin,
        t,
        canChangeStatus,
        statusOptions,
        newStatus,
        setNewStatus,
      ),
      title: t('rewards-gift:giftDetailsModal.giftCardDetailsTitle'),
    },
    {
      data: mapTransactionDetails(giftCard, timezone, t),
      title: t('rewards-gift:giftDetailsModal.transactionDetailsTitle'),
    },
    {
      data: mapRecipientDetails(giftCard, t),
      title: t('rewards-gift:giftDetailsModal.recipientDetailsTitle'),
    },
  ]

  useEffect(() => {
    if (!giftCard) {
      return
    }

    setNewStatus(giftCard?.status as GiftCardStatus)
  }, [giftCard])

  return (
    <Modal
      title={t('rewards-gift:giftDetailsModal.headerTitle')}
      footer={
        shouldShowSaveButton ? (
          <Button use="primary" size="small" onClick={handleChangeStatus}>
            {t('common:actions.save')}
          </Button>
        ) : null
      }
      onClose={onClose}
    >
      {isLoading ? (
        <div className="reward-loader-container">
          <BorderLoader align="absolute-center" size="medium" />
        </div>
      ) : (
        items.map((item, index) => <DetailsItem key={index} data={item.data} title={item.title} />)
      )}
    </Modal>
  )
}

export default RewardsGiftDetailsModal
